import AccountBoxIcon from '@mui/icons-material/AccountBox';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Button,
  FormControlLabel,
  Link,
  MenuItem,
  Tab,
  Theme,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import makeStyles from '@mui/styles/makeStyles';
import { Method } from 'axios';
import clsx from 'clsx';
import { Formik, FormikTouched } from 'formik';
import get from 'lodash/get';
import { DateTime, Duration } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import * as Yup from 'yup';

import {
  FormikSelect,
  FormikTextField,
  MaterialAutocomplete,
} from '../../../common/formik-material-ui';
import { FormikSwitch } from '../../../common/formik-material-ui/FormikSwitch';
import {
  loadableEnum,
  useMakePHPRequest,
} from '../../../common/useMakePHPRequest';
import { agentUserId } from '../../../recoil/atoms/agentUserId';
import {
  IBusinessUser,
  IUserAtom,
  Role,
  RoleLevel,
  user,
} from '../../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../../recoil/selectors/permissionsSelector';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { WizardModal } from '../../components/modals/WizardModal';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';

import { DeleteAgentModal } from './DeleteAgentModal';
import { getErrorTabIndex, isErrorInTab } from './DevicesAndRoutingModal';
import { ReplaceAgentModal } from './ReplaceAgentModal';
// import VoicemailIcon from '@mui/icons-material/Voicemail';
// import SmsIcon from '@mui/icons-material/Sms';
// import ContactsIcon from '@mui/icons-material/Contacts';

import { useGetActiveUserProductsQuery } from '../../../api/apigQuery';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import '../../../common/formik-material-ui/checkFileSize';
import '../../../common/formik-material-ui/commaSeparatedEmails';
import { POWERDIALER_MACHINE_DETECT_PRODUCT_ID } from '../../pages/ManageAccount';
import { createHoursString, Scheduler } from '../CadenceSettings/Scheduler';
import {
  DefaultAction,
  getDefaultActionPayload,
  getDefaultActionUrl,
} from '../dropdowns/DefaultAction';

// import { AddNumberModalTab } from './AddNumberModalTab';
// import { AddVMDropModalTab } from './AddVMDropModalTab';
// import { AddSMSTemplateTab } from './AddSMSTemplateTab';
import {
  trackModalUpdates,
  trackSaveEditAgent,
} from '../../../common/userTracking';
import { AdvancedIcon, UserSettingsIcon } from '../../../images/SettingsIcons';
import TabLabel from '../general/TabLabel';
import {
  getBillingTiers,
  getPowerlistDialerProductMap,
  products,
} from '../products';

import { ConfirmAgentPowerlists } from './ConfirmAgentPowerlists';
import ManageAgentProductsModal from './ManageAgentProductsModal';
import { ModalWithStyles } from './ModalWithStyles';
import ProductChangesConfirmModal from './ProductChangesConfirmModal';

const useStyles = makeStyles((theme: Theme) => ({
  tabs: {},
  tabIcon: {
    marginRight: 15,
  },
  downgradeMessage: {
    color: theme.palette.mode === 'dark' ? 'greenyellow' : 'green',
  },
  editButton: {
    maxWidth: '155px',
    maxHeight: '50px',
    minWidth: '155px',
    minHeight: '50px',
  },
  tab: {
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
  },
  header: {
    color: 'var(--primary)', // pull color from primary
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  warningIcon: {
    color: 'orange',
  },
  modal: {
    paddingBottom: 0,
    maxHeight: 'calc(100vh - 210px)',
    overflowY: 'auto',
  },
  defaultAction: {
    marginTop: theme.spacing(2),
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  spinner: {
    position: 'fixed !important' as 'fixed',
  },
  buttonContainedRed: {
    color: theme?.custom?.button?.delete?.contrastText,
    backgroundColor: theme?.custom?.button?.delete?.main,
    marginLeft: theme.spacing(1), // space between fields
    '&:hover': {
      backgroundColor: theme?.custom?.button?.delete?.dark,
    },
  },
  helpText: {
    justifyContent: 'flex-start',
    marginTop: '1.0vh',
  },
  outlineHeight: {
    height: 'fit-content',
  },
}));

interface EditAgentModalProps {
  id: string;
  show: boolean;
  onHide(): void;
  initialTabIndex?: number;
}

export function EditAgentModal({
  id,
  show,
  onHide,
  initialTabIndex = 0,
}: EditAgentModalProps) {
  const history = useHistory();
  // state for delete agent modal
  const [shows, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  // state for replace modal
  const [showR, setShowR] = useState(false);
  const handleCloseR = () => setShowR(false);
  const handleShowR = () => setShowR(true);
  const [flagForReplace, setFlagForReplace] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showDowngrade, setShowDowngrade] = useState(false);
  const [userProductChanges, setUserProductChanges] = useState<
    { userId: number; product: string }[]
  >([]);
  const [showProductChanges, setShowProductChanges] = useState(false);
  const [productChangesLoading, setProductChangesLoading] = useState(false);
  const [showManageAgentProducts, setShowManageAgentProducts] = useState(false);
  const [formikValues, setFormikValues] = useState({});
  const setUserId = useSetRecoilState(agentUserId);
  // const [numberToBuy, setNumberToBuy] = useState(0);
  // const [rate, setRate] = useState(0);
  const {
    role,
    canAccessPowerlistProduct,
    canViewSetSuperadminPermissions,
    hasSetSuperadminPermissions,
    enableFreeBillingTier,
    isBusinessEligibleToAccessCallTranscriptions,
    isMachineDetectionFeatureEnabled,
    inTrial,
  } = useRecoilValueLoadable<IPermissions>(permissionsSelector)
    .contents as IPermissions;
  const data = {
    call: methodNameEnum.GET_BUSINESS_DATA,
  };
  const businessDataLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const businessData =
    businessDataLoadable?.contents?.response?.data?.result?.[0];
  const showPauseCallRecording = useMemo(
    () => businessData?.allowPauseRecording === 1,
    [businessData]
  );
  const permissionMemo = useMemo(() => {
    if (businessData && businessData?.canDeleteUsers === 1) {
      return true;
    } else {
      if (role === Role.SUPERADMIN) {
        return true;
      } else {
        return false;
      }
    }
  }, [businessData, role]);

  const setRefetchToggleForCurrentAgentDetails = useSetRecoilState(
    refetchToggle('currentAgentDetails')
  );
  const setRefetchToggleForAgents = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_USERS)
  );
  const setRefetchToggleForNumbers = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_NUMBERS)
  );
  const setRefetchToggleForAgentNumbers = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_DIDS)
  );

  const classes = useStyles();
  const formikTouched = useRef<FormikTouched<any>>();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [submitState, setSubmitState] = useState(loadableEnum.EMPTY);
  // const [vmDropRecording, setVmDropRecording] = useState(false);
  const makeRequest = useMakePHPRequest({
    userAtom: userAtomContents,
  });
  // // VM Drop Modal Code
  // const [uploadedVMFiles, setUploadedVMFiles] = useState({
  //   voicemail: '',
  // });
  // const [VMFilenames, setVMFilenames] = useState({
  //   voicemail: '',
  // });
  // const [VMFilepaths, setVMFilepaths] = useState({
  //   voicemail: '',
  // });
  // const setRefetchToggleForVmDrop = useSetRecoilState(
  //   refetchToggle(endpointEnum.GET_VM_DROP)
  // );
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  const agentSchema = useMemo(
    () =>
      Yup.object().shape({
        accountSettings: Yup.object({
          email: Yup.string()
            .commaSeparatedEmails('Must be a email')
            .required('Required'),
          // .7('This Email Address is Already in Use'),
          // need to fix this
          fname: Yup.string().required('Required'),
          lname: Yup.string().required('Required'),
          callerIdName: Yup.string()
            .required('Required')
            .max(60, 'Maximum 60 characters'),
        }),
        settings: Yup.object({
          userExt: Yup.number().required('Required'),
        }),
        buyNumber: Yup.object({
          numberName: Yup.string().max(
            100,
            'Name must be a maximum of 100 characters'
          ),
        }),
        // VMDrop: vmDropRecording
        //   ? Yup.object().shape({
        //       VMDropName: Yup.string().max(100, 'Maximum 100 characters'),
        //     })
        //   : VMFilenames.voicemail
        //   ? Yup.object().shape(
        //       {
        //         VMDropName: Yup.string().required('Required'),
        //         mp3Name: Yup.string()
        //           .required('REQED!!!')
        //           .checkFileSize(20_000_000, 'File must be at or below 20mb'),
        //       },
        //       [['VMDropName', 'mp3Name']]
        //     )
        //   : Yup.object().shape(
        //       {
        //         VMDropName: Yup.string(),
        //         mp3Name: Yup.string()
        //           .ensure()
        //           .when('VMDropName', {
        //             is: (val) => val !== undefined,
        //             then: Yup.string()
        //               .required('Required')
        //               .checkFileSize(
        //                 20_000_000,
        //                 'File must be at or below 20mb'
        //               ),
        //           }),
        //       },
        //       [['VMDropName', 'mp3Name']]
        //     ),
        // SMSTemplate: Yup.object().shape(
        //   {
        //     name: Yup.string()
        //       .ensure()
        //       .when('content', {
        //         is: (val) => val !== '',
        //         then: Yup.string()
        //           .required('Required')
        //           .max(100, 'Maximum 100 characters'),
        //       }),
        //     content: Yup.string()
        //       .ensure()
        //       .when('name', {
        //         is: (val) => val !== '',
        //         then: Yup.string()
        //           .required('Required')
        //           .max(512, 'Maximum 512 characters'),
        //       }),
        //   },
        //   [['name', 'content']]
        // ),
      }),
    // [VMFilenames, vmDropRecording]
    []
  );

  const [
    myBusinessUsersCached,
    myBusinessUsersLoadable,
    refetchToggleMyBusinessUsers,
  ] = makeApiRequestCached<IBusinessUser | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.find(
        (user) => user.userid == id
      ) ?? cached.current,
    undefined,
    [id]
  );

  const {
    data: activeUserProducts,
    isFetching: getActiveUserProductsLoading,
    refetch: refetchActiveUserProducts,
    isUninitialized: isUninitializedActiveUserProducts,
  } = useGetActiveUserProductsQuery(
    {
      userSettings: {
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
        userId: id,
        userid: id,
        apikey: userAtomContents?.apikey,
      },
      userIds: [Number(id)],
    },
    { skip: !userAtomContents?.businessid }
  );

  useEffect(() => {
    refetchToggleMyBusinessUsers({});
    // refetchActiveUserProducts();
    !isUninitializedActiveUserProducts && refetchActiveUserProducts();
  }, []);
  const [businessNumbersCached, businessNumbersLoadable] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_NUMBERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_NUMBERS,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result
        ?.filter(
          (item) =>
            item.type.toLowerCase() === 'queue' ||
            item.type.toLowerCase() === 'ivr' ||
            item.type.toLowerCase() === 'group' ||
            item.userid == id
        )
        .reduce(
          (acc, item) => ({
            ...acc,
            myNumbers:
              item.userid == id && item.sms
                ? acc.myNumbers.concat(item.DID)
                : acc.myNumbers,
            allNumbers: acc.allNumbers.concat(item.DID),
            agentNumbers:
              item.userid == id
                ? acc.agentNumbers.concat(item.DID)
                : acc.agentNumbers,
            queueNumbers:
              item.type.toLowerCase() === 'queue'
                ? acc.queueNumbers.concat(item.DID)
                : acc.queueNumbers,
            ivrNumbers:
              item.type.toLowerCase() === 'ivr'
                ? acc.ivrNumbers.concat(item.DID)
                : acc.ivrNumbers,
            groupNumbers:
              item.type.toLowerCase() === 'group'
                ? acc.groupNumbers.concat(item.DID)
                : acc.groupNumbers,
            lookupNumbers: {
              ...acc.lookupNumbers,
              [item.DID]: item,
            },
          }),
          {
            myNumbers: [],
            allNumbers: [],
            agentNumbers: [],
            queueNumbers: [],
            ivrNumbers: [],
            groupNumbers: [],
            lookupNumbers: {},
          }
        ) ?? cached.current,
    {
      myNumbers: [],
      allNumbers: [],
      agentNumbers: [],
      queueNumbers: [],
      ivrNumbers: [],
      groupNumbers: [],
      lookupNumbers: {},
    },
    [id]
  );
  const [businessCached, businessLoadable] = makeApiRequestCached(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userAtomContents?.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.map((business) => ({
        ...business,
        businessHarassmentPolicies: business.businessHarassmentPolicies?.map(
          (policy) => {
            const luxonDuration = Duration.fromMillis(
              policy.durationMin * 60 * 1000
            )
              .shiftTo('days', 'hours', 'minutes', 'seconds', 'milliseconds')
              .toObject();
            return {
              ...policy,
              days: luxonDuration.days,
              hours: luxonDuration.hours,
              minutes: luxonDuration.minutes,
            };
          }
        ),
      }))?.[0] ?? cached.current
  );

  const businessHoursMemo = useMemo(
    () =>
      businessCached.current?.businessDetails?.businessHours ?? {
        1: {
          day: 'Monday',
          hours: [],
        },
        2: {
          day: 'Tuesday',
          hours: [],
        },
        3: {
          day: 'Wednesday',
          hours: [],
        },
        4: {
          day: 'Thursday',
          hours: [],
        },
        5: {
          day: 'Friday',
          hours: [],
        },
        6: {
          day: 'Saturday',
          hours: [],
        },
        7: {
          day: 'Sunday',
          hours: [],
        },
      },
    [businessLoadable]
  );

  // state for business hours modal;
  const [showBusinessHours, setShowBusinessHours] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const handleShowScheduler = () => setShowScheduler(true);
  const onHideHours = () => {
    setShowScheduler(false);
  };

  const schedulableHoursCached = useRef();
  const handleSetSchedulableHours = (hours) => {
    schedulableHoursCached.current = hours;
  };

  const [tabIndex, setTabIndex] = useState(initialTabIndex);
  const handleChange = (formik) => async (event, newIndex) => {
    setTabIndex(+newIndex);
  };

  const filterOptions = createFilterOptions({
    stringify: (option: string) =>
      `${option} ${
        (businessNumbersCached.current?.lookupNumbers[option] as any)?.type
      } ${
        (businessNumbersCached.current?.lookupNumbers[option] as any)
          ?.destinationName
      }`,
  });
  const tabNames = useMemo(
    () =>
      role !== Role.USER
        ? [
            {
              title: 'Profile',
              icon: AccountBoxIcon,
              dataPup: 'edit-agent-profile',
            },
            {
              title: 'User Hours',
              icon: QueryBuilderIcon,
              dataPup: 'edit-agent-user-hours',
            },
            {
              title: 'User Products',
              icon: SettingsApplicationsIcon,
              dataPup: 'edit-agent-user-products',
            },
            {
              title: 'User Settings',
              icon: UserSettingsIcon,
              dataPup: 'edit-agent-user-settings',
            },
            {
              title: 'Advanced',
              icon: AdvancedIcon,
              dataPup: 'edit-agent-advanced',
            },
          ]
        : [
            {
              title: 'Profile',
              icon: AccountBoxIcon,
              dataPup: 'profile',
            },
            {
              title: 'User Hours',
              icon: QueryBuilderIcon,
              dataPup: 'user-hours',
            },
            {
              title: 'User Settings',
              icon: UserSettingsIcon,
              dataPup: 'edit-agent-user-settings',
            },
            {
              title: 'Advanced',
              icon: AdvancedIcon,
              dataPup: 'edit-agent-advanced',
            },
          ],
    [role, userAtomLoadable]
  );
  const currentTabTitle = useMemo(() => {
    return tabNames[tabIndex]?.title ?? '';
  }, [tabIndex, tabNames]);
  const disableUserType =
    RoleLevel[role] >
    RoleLevel[myBusinessUsersCached.current?.type ?? Role.USER]; // disable role dropdown if viewed user role has higher privilege (0 === superadmin, 30 = user)
  const roleDropdown = useMemo(
    () =>
      // iterate over values in dropdown and determine whether each role will be shown/hide and selectable/unselectable.
      Object.keys(RoleLevel).map((roleName) => ({
        role: roleName, // role name: eg. superadmin, admin, manager, user
        disabled: RoleLevel[role] > RoleLevel[roleName], // disable if viewer role has lower privilege than viewed user (0 === superadmin, 30 = user)
        hide:
          roleName === Role.SUPERADMIN &&
          userAtomContents?.businessid !== 995 &&
          userAtomContents?.businessid !== 21594, // hide superadmin unless business is 995 is 995 or 21594
      })),
    [role, myBusinessUsersLoadable]
  );

  // SMS Template Code
  // const setRefetchToggleForSmsTemplates = useSetRecoilState(
  //   refetchToggle(endpointEnum.GET_SMS_TEMPLATE)
  // );

  useEffect(() => {
    if (myBusinessUsersCached.current?.userid != Number(id)) {
      setUserId({
        id,
        name: `${myBusinessUsersCached.current?.fname} ${myBusinessUsersCached.current?.lname}`,
        systemTeamId: myBusinessUsersCached?.current?.systemTeamId ?? '',
        type: '',
      });

      setRefetchToggleForCurrentAgentDetails({});
    }
  }, [myBusinessUsersLoadable]);

  const billingTiers = getBillingTiers(Role, role);

  const powerlistDialerProductMap = getPowerlistDialerProductMap(
    POWERDIALER_MACHINE_DETECT_PRODUCT_ID
  );
  const tabToFields = [
    ['accountSettings'],
    [],
    ['manage'],
    ['settings'],
    ['buyNumber'],
    ['VMDrop'],
    ['SMSTemplate'],
  ];
  // check if Call Transcription product has been updated
  const hasCallTranscriptionFeaturedEnabled =
    process.env.VITE_APP_FEATURE_CALL_TRANSCRIPTIONS === '1' &&
    isBusinessEligibleToAccessCallTranscriptions === true;

  const [agentUserIdAtom] = useRecoilStateLoadable(agentUserId);
  const submitForm = async (val, formValues) => {
    if (formValues && Object.keys(formValues).length !== 0) {
      setSubmitState(loadableEnum.LOADING);
      const values: any = formValues;
      const {
        fname,
        lname,
        callerIdName,
        outboundCallerId,
        outboundSmsNumber,
        email,
        userType,
        billingTier,
        minutesPlan,
        othersCanViewRec,
        callWaiting,
        recordCalls,
        canCallCoach,
        canBeManaged,
        dnd,
        localP,
        vmDrop,
        ftcBlocking,
        useBusinessHoursEnabled,
        userHours,
        closedActionPayload,
        defaultExtend,
        agentQueueEnabled,
        logLevelEnabled,
        overrideDnc,
        setSuperadminPermissions,
        switchUser,
        canPauseRecording,
      } = values?.accountSettings ?? {};
      const updatePdCallTranscriptionProduct =
        values.manage.hasPdCallTranscription !==
        Boolean(myBusinessUsersCached.current?.hasPdCallTranscription);
      const { userExt } = values?.settings ?? {};
      let userid = myBusinessUsersCached.current?.userid;
      if (flagForReplace) {
        const resultReplace = await makeRequest({
          methodName: methodNameEnum.REPLACE_USER,
          payload: {
            fname,
            lname,
            email,
            calleridNumber: outboundCallerId,
            callerid: callerIdName,
            smsNumber: outboundSmsNumber,
            userid: myBusinessUsersCached.current?.userid,
            useBusinessHoursEnabled: useBusinessHoursEnabled ? '1' : '0',
            userHours: JSON.stringify(userHours),
            closedAction: getDefaultActionUrl({
              type: closedActionPayload?.type,
              parentType: 'user',
              businessid: userAtomContents.businessid,
              user: closedActionPayload?.user,
              group: closedActionPayload?.group,
              ivr: closedActionPayload?.ivr,
              queue: closedActionPayload?.queue,
              voicemail: id,
            }),
          },
        });
        // make call to replaece agent and extract the new user id of the agent just created and inject into new requests only if the flagforreplacing the agent is present
        userid =
          resultReplace?.response?.data?.result?.userid ??
          myBusinessUsersCached.current?.userid;
      }
      const payload = {
        fname,
        lname,
        email,
        calleridNumber: outboundCallerId,
        callerid: callerIdName,
        smsNumber: outboundSmsNumber,
        userid,
        useBusinessHoursEnabled: useBusinessHoursEnabled ? '1' : '0',
        userHours: JSON.stringify(userHours),
        closedAction: getDefaultActionUrl({
          type: closedActionPayload?.type,
          parentType: 'user',
          businessid: userAtomContents.businessid,
          user: closedActionPayload?.user,
          group: closedActionPayload?.group,
          ivr: closedActionPayload?.ivr,
          queue: closedActionPayload?.queue,
          voicemail: id,
        }),
      };
      // submit first column payload
      const agentSettings = await makeRequest({
        methodName: methodNameEnum.EDIT_AGENT_PROFILE,
        payload: payload,
      });

      let hasUserSettingsUpdated = false;
      // Check to see if the PowerDialer Product has been updated
      if (
        ((myBusinessUsersCached.current?.powerlistEndDate &&
        !myBusinessUsersCached.current?.nextPowerlistProductId
          ? 0 // if current powerlist has enddate without next powerlist then 0 to indicate no previous powerlist
          : myBusinessUsersCached.current?.nextPowerlistProductId) ??
          myBusinessUsersCached.current?.powerlistProductId ?? // if no current powerlist then no previous powerlist
          0) !== values?.manage.powerlistProduct // compare to current chosen powerlist
      ) {
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_POWERLIST_PRODUCT,
          payload: {
            call: 'updatePowerlistProduct',
            productValue: values.manage.powerlistProduct === 0 ? 0 : 1,
            productid:
              values.manage.powerlistProduct === 0
                ? myBusinessUsersCached.current?.powerlistProductId
                : values.manage.powerlistProduct,
            userid,
          },
        });
        hasUserSettingsUpdated = true;
      }
      const businessId = userAtomContents.businessid?.toString() ?? '';
      const userId = userid?.toString() ?? '';
      if (
        hasCallTranscriptionFeaturedEnabled &&
        updatePdCallTranscriptionProduct &&
        businessId &&
        userId
      ) {
        const request = {
          method: 'PUT' as Method,
          domain: process.env.VITE_APP_CDK_DOMAIN,
          stage: 'auto',
          endpoint: endpointEnum.CALL_TRANSCRIPTION_PRODUCT.replace(
            '{businessId}',
            businessId
          ).replace('{userId}', userId),
          payload: {
            hasProduct: Number(values.manage.hasPdCallTranscription),
          },
        } as const;
        await mutation.mutateAsync(request);
      }

      const data = {
        tierprocess: 'change',
        extension: userExt ?? myBusinessUsersCached?.current?.userEXT ?? 0,
        usertype: userType,
        tier: billingTier,
        unlimited: minutesPlan,
        userid,
        useBusinessHoursEnabled: useBusinessHoursEnabled ? '1' : '0',
      };
      await makeRequest({
        methodName: methodNameEnum.UPDATE_MANAGEMENT_OPTIONS,
        payload: data,
      });
      if (
        othersCanViewRec !==
        (myBusinessUsersCached.current?.othersCanViewRecordings === 1)
      ) {
        // const { _response5 } = await makeRequest({
        //   methodName: methodNameEnum.OTHERS_CAN_VIEW_RECORDINGS,
        //   payload: {
        // value: othersCanViewRec ? '1' : '0',
        // userid,
        //   },
        // });
        await mutation.mutateAsync({
          endpoint: endpointEnum.OTHERS_CAN_VIEW_RECORDINGS,
          payload: {
            call: methodNameEnum.OTHERS_CAN_VIEW_RECORDINGS,
            name: methodNameEnum.OTHERS_CAN_VIEW_RECORDINGS,
            value: othersCanViewRec ? '1' : '0',
            userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }

      if (overrideDnc !== !!myBusinessUsersCached.current?.overrideDnc) {
        // const { _response6 } = await makeRequest({
        //   methodName: methodNameEnum.UPDATE_USER_2,
        // payload: {
        //   name: 'allowMultipleIncomingCalls',
        //   value: callWaiting ? '1' : '0',
        //   userid,
        // },
        // });
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_USER_2,
          payload: {
            call: methodNameEnum.UPDATE_USER_2,
            name: 'overrideDnc',
            value: overrideDnc ? '1' : '0',
            userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
        hasUserSettingsUpdated = true;
      }

      if (
        setSuperadminPermissions !==
        !!myBusinessUsersCached.current?.setSuperadminPermissions
      ) {
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_SUPERADMIN_SETTINGS,
          payload: {
            call: methodNameEnum.UPDATE_SUPERADMIN_SETTINGS,
            name: 'setSuperadminPermissions',
            value: setSuperadminPermissions ? '1' : '0',
            userid,
            superadminuserid: userAtomContents.userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }
      if (switchUser !== !!myBusinessUsersCached.current?.switchUser) {
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_SUPERADMIN_SETTINGS,
          payload: {
            call: methodNameEnum.UPDATE_SUPERADMIN_SETTINGS,
            name: 'switchUser',
            value: switchUser ? '1' : '0',
            userid,
            superadminuserid: userAtomContents.userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }
      if (
        callWaiting !==
        (myBusinessUsersCached.current?.allowMultipleIncomingCalls === 1)
      ) {
        // const { _response6 } = await makeRequest({
        //   methodName: methodNameEnum.UPDATE_USER_2,
        // payload: {
        //   name: 'allowMultipleIncomingCalls',
        //   value: callWaiting ? '1' : '0',
        //   userid,
        // },
        // });
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_USER_2,
          payload: {
            call: methodNameEnum.UPDATE_USER_2,
            name: 'allowMultipleIncomingCalls',
            value: callWaiting ? '1' : '0',
            userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }
      if (recordCalls !== (myBusinessUsersCached.current?.recordCalls === 1)) {
        // const { _response6 } = await makeRequest({
        //   methodName: methodNameEnum.UPDATE_USER_2,
        // payload: {
        //   name: 'recordCalls',
        //   value: recordCalls ? '1' : '0',
        //   userid,
        // },
        // });
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_USER_2,
          payload: {
            call: methodNameEnum.UPDATE_USER_2,
            name: 'recordCalls',
            value: recordCalls ? '1' : '0',
            userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }
      if (
        canCallCoach !==
        (myBusinessUsersCached.current?.canCallCoach === 1)
      ) {
        // const { _response6 } = await makeRequest({
        //   methodName: methodNameEnum.UPDATE_USER_2,
        //   payload: {
        // name: 'canCallCoach',
        // value: canCallCoach ? '1' : '0',
        // userid,
        //   },
        // });
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_USER_2,
          payload: {
            call: methodNameEnum.UPDATE_USER_2,
            name: 'canCallCoach',
            value: canCallCoach ? '1' : '0',
            userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }
      if (
        defaultExtend !==
        (myBusinessUsersCached.current?.defaultExtend === 1)
      ) {
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_USER_2,
          payload: {
            call: methodNameEnum.UPDATE_USER_2,
            name: 'defaultExtend',
            value: defaultExtend ? '1' : '0',
            userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }
      if (
        canBeManaged !==
        (myBusinessUsersCached.current?.canBeManaged === 1)
      ) {
        // await makeRequest({
        //   methodName: methodNameEnum.UPDATE_USER_2,
        //   payload: {
        // name: 'canBeManaged',
        // value: canBeManaged ? '1' : '0',
        // userid,
        //   },
        // });
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_USER_2,
          payload: {
            call: methodNameEnum.UPDATE_USER_2,
            name: 'canBeManaged',
            value: canBeManaged ? '1' : '0',
            userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }
      if (dnd !== (myBusinessUsersCached.current?.DND === 1)) {
        // const { _response6 } = await makeRequest({
        //   methodName: methodNameEnum.UPDATE_USER_2,
        //   payload: {
        // name: 'dnd',
        // value: dnd ? '1' : '0',
        // userid,
        //   },
        // });
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_USER_2,
          payload: {
            call: methodNameEnum.UPDATE_USER_2,
            name: 'dnd',
            value: dnd ? '1' : '0',
            userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }
      if (localP !== (myBusinessUsersCached.current?.localPresenceLite === 1)) {
        // const { _response6 } = await makeRequest({
        //   methodName: methodNameEnum.UPDATE_LOCAL_PRESENCE,
        //   payload: {
        // value: localP ? '1' : '0',
        // userid,
        //   },
        // });
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_LOCAL_PRESENCE,
          payload: {
            call: methodNameEnum.UPDATE_LOCAL_PRESENCE,
            name: methodNameEnum.UPDATE_LOCAL_PRESENCE,
            value: localP ? '1' : '0',
            userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }
      if (vmDrop !== myBusinessUsersCached.current?.vmDropDetectionType) {
        // const { _response6 } = await makeRequest({
        //   methodName: methodNameEnum.UPDATE_USER_2,
        //   payload: {
        // name: 'vmDropDetectionType',
        // value: vmDrop,
        // userid,
        //   },
        // });
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_USER_2,
          payload: {
            call: methodNameEnum.UPDATE_USER_2,
            name: 'vmDropDetectionType',
            value: vmDrop,
            userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }
      if (
        canPauseRecording !==
        Boolean(myBusinessUsersCached.current?.canPauseRecording)
      ) {
        const payload = {
          call: methodNameEnum.UPDATE_USER_2,
          name: 'canPauseRecording',
          value: canPauseRecording,
          userid,
          apikey: userAtomContents.apikey,
          businessid: userAtomContents.businessid,
        };
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_USER_2,
          payload: payload,
        });
      }
      await makeRequest({
        methodName: methodNameEnum.FTC_BLOCKING,
        payload: {
          value: ftcBlocking ? '1' : '0',
          userid,
        },
      });
      if (val) {
        setShowManageAgentProducts(true);
        setProductChangesLoading(false);
        setShowProductChanges(false);
      }
      // if the flag to replace is present reroute to the newly created agents call reporting otherwise we assume we are editing the same agent and reroute to agent being viewed call reporting
      if (flagForReplace && userid) {
        history.push(`/manage/agents/${userid}/agent-reporting`);
      } else {
        if (!val) {
          setProductChangesLoading(false);
          onHide();
          setFormikValues({});
        }
      }
      setRefetchToggleForCurrentAgentDetails({});
      if (
        agentSettings?.response?.data?.meta?.message?.includes('Duplicate') &&
        !agentSettings?.response?.data?.meta?.success
      ) {
        enqueueSnackbarWithStyles({
          message: `An agent with the email address ${email} already exists. Changes not saved.`,
          alertProps: { severity: 'error' },
        });
      } else {
        enqueueSnackbarWithStyles({
          message: 'Successfully Updated Settings',
          alertProps: { severity: 'success' },
        });
      }
      if (
        agentQueueEnabled !==
        (myBusinessUsersCached.current?.agentQueueEnabled === 1)
      ) {
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_USER_2,
          payload: {
            call: methodNameEnum.UPDATE_USER_2,
            name: 'agentQueueEnabled',
            value: agentQueueEnabled ? '1' : '0',
            userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }
      if (
        logLevelEnabled !==
        (myBusinessUsersCached.current?.logLevel === 'debug')
      ) {
        await mutation.mutateAsync({
          endpoint: endpointEnum.UPDATE_USER_2,
          payload: {
            call: methodNameEnum.UPDATE_USER_2,
            name: 'logLevel',
            value: logLevelEnabled ? 'debug' : 'off',
            userid,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
          },
        });
      }

      if (
        hasCallTranscriptionFeaturedEnabled &&
        updatePdCallTranscriptionProduct &&
        businessId &&
        userId
      ) {
        const result = await mutation.mutateAsync({
          method: 'GET',
          domain: process.env.VITE_APP_CDK_DOMAIN,
          endpoint: endpointEnum.GET_USER_PRODUCTS.replace(
            '{businessId}',
            businessId
          ).replace('{userId}', userId),
        });
        if (result.data) {
          localStorage.setItem('userProducts', JSON.stringify(result.data));
        }
      }

      if (hasUserSettingsUpdated && businessId && userId) {
        const request = {
          method: 'POST' as Method,
          domain: process.env.VITE_APP_CDK_DOMAIN,
          stage: 'auto',
          endpoint: endpointEnum.USER_SETTINGS_UPDATED.replace(
            '{businessId}',
            businessId
          ).replace('{userId}', userId),
        } as const;
        await mutation.mutateAsync(request);
      }
      // if (numberToBuy) {
      //   const { response } = await makeRequest({
      //     methodName: methodNameEnum.BUY_PHONE_NUMBER,
      //     payload: {
      //       // targetid: newAgentResult?.data?.result?.userid,
      //       targetid: id,
      //       destinationType: 'user',
      //       number: numberToBuy,
      //       rate,
      //       numType:
      //         values.buyNumber.numType.toLocaleLowerCase() === 'local'
      //           ? 'fixed'
      //           : values.buyNumber.numType.toLocaleLowerCase(),
      //       numberName: values.buyNumber.numberName,
      //     },
      //   });
      //   if (response?.data?.success === 'true') {
      //     enqueueSnackbarWithStyles({
      //       message: `Successfully Purchased the Number - ${numberToBuy}`,
      //       alertProps: { severity: 'success' },
      //     });
      //   } else {
      //     enqueueSnackbarWithStyles({
      //       message: 'Encountered An Error while trying to purchase Number',
      //       alertProps: { severity: 'warning' },
      //     });
      //   }
      //   // Finish Buy Number

      //   // resetForm({});
      //   // actions.resetForm({});
      //   // setRefetchToggleForNumbers({});
      //   // handleCloseOuter();
      // }
      // Start Add VM Drop
      // if (values.VMDrop.VMDropName) {
      //   if (values.VMDrop.mp3Type === 'record') {
      //     const data = new FormData();
      //     data.append('webm', uploadedVMFiles.voicemail[0]);
      //     data.append(
      //       'businessid',
      //       (userAtomContents.businessid as unknown) as string
      //     );
      //     data.append('apikey', userAtomContents.apikey);
      //     data.append('userid', (userid as unknown) as string);
      //     data.append('call', 'HandleAudioRecording');
      //     data.append('id', (userid as unknown) as string);
      //     data.append('iid', (0 as unknown) as string);
      //     data.append('name', values.VMDrop.VMDropName);
      //     data.append('type', 'new-agent-voicemail-drop');
      //     const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
      //     await Axios.post(url, data, { withCredentials: true });
      //   }
      //   if (values.VMDrop.mp3Type === 'mp3' && uploadedVMFiles.voicemail) {
      //     const data = new FormData();
      //     data.append('call', 'submitVMDropMp3File');
      //     data.append(
      //       'businessid',
      //       (userAtomContents.businessid as unknown) as string
      //     );
      //     data.append('apikey', userAtomContents.apikey);
      //     data.append('userid', (userid as unknown) as string);
      //     data.append('vmdropName', values.VMDrop.VMDropName);
      //     data.append('mp3', uploadedVMFiles.voicemail[0]);
      //     const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
      //     await Axios.post(url, data, { withCredentials: true });
      //   }
      //   enqueueSnackbarWithStyles({
      //     message: `Successfully Added ${values.VMDrop.VMDropName} VM Drop`,
      //     alertProps: { severity: 'success' },
      //   });
      // }

      // // Start add sms template
      // if (values.SMSTemplate.name) {
      //   const { name, content } = values.SMSTemplate;
      //   const data = {
      //     call: methodNameEnum.SUBMIT_NEW_TEMPLATE,
      //     // apikey: userAtomContents?.apikey,
      //     // businessid: storageUser?.businessid,
      //     name,
      //     content,
      //     userid,
      //   };
      //   const response = await mutation.mutateAsync({
      //     endpoint: endpointEnum.SUBMIT_NEW_TEMPLATE,
      //     payload: data,
      //   });
      //   if (response?.data?.success) {
      //     enqueueSnackbarWithStyles({
      //       message: 'Successfully Added SMS Template',
      //       alertProps: { severity: 'success' },
      //     });
      //   } else {
      //     enqueueSnackbarWithStyles({
      //       message: 'Encountered an error adding the SMS Template',
      //       alertProps: { severity: 'error' },
      //     });
      //   }
      // }
      // history.push('manage/agents');
      setSubmitState(loadableEnum.HAS_VALUE);
      // setRefetchToggleForSmsTemplates({});
      // setRefetchToggleForVmDrop({});
      setRefetchToggleForAgents({});
      setRefetchToggleForNumbers({});
      setRefetchToggleForAgentNumbers({});
      setFlagForReplace(false);
    }
  };
  const agentInitialValues = {
    accountSettings: {
      fname: myBusinessUsersCached.current?.fname ?? '',
      lname: myBusinessUsersCached.current?.lname ?? '',
      callerIdName: myBusinessUsersCached?.current?.calleridName ?? '',
      outboundCallerId: myBusinessUsersCached.current?.callerid ?? '',
      outboundSmsNumber: myBusinessUsersCached.current?.smsDID ?? '',
      email: myBusinessUsersCached.current?.email ?? '',
      userType: myBusinessUsersCached.current?.type ?? '',
      billingTier:
        billingTiers[
          myBusinessUsersCached.current?.nextUserTier ||
            (myBusinessUsersCached.current?.userTier as string)
        ]?.tier ?? '',
      minutesPlan:
        billingTiers[
          myBusinessUsersCached.current?.nextUserTier ||
            (myBusinessUsersCached.current?.userTier as string)
        ]?.unlimited ?? '',
      othersCanViewRec:
        myBusinessUsersCached.current?.othersCanViewRecordings === 1,
      callWaiting:
        myBusinessUsersCached.current?.allowMultipleIncomingCalls === 1,
      recordCalls: myBusinessUsersCached.current?.recordCalls === 1,
      canCallCoach: myBusinessUsersCached.current?.canCallCoach === 1,
      defaultExtend: myBusinessUsersCached.current?.defaultExtend === 1,
      canBeManaged: myBusinessUsersCached.current?.canBeManaged === 1,
      dnd: myBusinessUsersCached.current?.DND === 1,
      localP: myBusinessUsersCached.current?.localPresenceLite === 1,
      vmDrop: myBusinessUsersCached.current?.vmDropDetectionType ?? 'beep',
      ftcBlocking: myBusinessUsersCached.current?.ftcBlocking === 1,
      userHours: myBusinessUsersCached.current?.userHours
        ? JSON.parse(myBusinessUsersCached.current?.userHours ?? 'null')
        : businessHoursMemo,
      useBusinessHoursEnabled: !!get(
        myBusinessUsersCached,
        `current.useBusinessHoursEnabled`,
        true
      ),
      closedActionPayload: getDefaultActionPayload(
        myBusinessUsersCached.current?.closedAction ?? undefined,
        true,
        undefined,
        id
      ),
      agentQueueEnabled: myBusinessUsersCached.current?.agentQueueEnabled === 1,
      logLevelEnabled: myBusinessUsersCached.current?.logLevel === 'debug',
      overrideDnc: myBusinessUsersCached.current?.overrideDnc === 1,
      setSuperadminPermissions:
        myBusinessUsersCached.current?.setSuperadminPermissions === 1,
      switchUser: myBusinessUsersCached.current?.switchUser === 1,
      canPauseRecording: myBusinessUsersCached.current?.canPauseRecording === 1,
    },
    manage: {
      // show next powerlist or if powerlist is ending then show none
      powerlistProduct:
        myBusinessUsersCached.current?.nextPowerlistProductId ||
        (myBusinessUsersCached.current?.powerlistEndDate
          ? 0
          : myBusinessUsersCached.current?.powerlistProductId || 0),
      hasPdCallTranscription:
        myBusinessUsersCached.current?.hasPdCallTranscription === 1,
    },
    settings: {
      userExt: myBusinessUsersCached.current?.userEXT ?? '',
    },
    buyNumber: {
      numberFor: 'None',
      agent: '',
      conference: '',
      group: '',
      ivr: '',
      queue: '',
      country: 'US',
      areaCode: '',
      smsAble: 'Y',
      numType: 'Local',
      availableNumbers: [],
      numberName: '',
    },
    VMDrop: {
      VMDropName: '',
      mp3Type: 'mp3',
      mp3File: '',
      mp3Name: '',
    },
    SMSTemplate: {
      name: '',
      content: '',
    },
  };
  function trackUpdates(values, agentInitialValues, currentTabTitle) {
    trackModalUpdates(
      values,
      agentInitialValues,
      'Agent Settings Updated',
      currentTabTitle
    );
  }
  return (
    <Formik
      validationSchema={agentSchema}
      onSubmit={async (values, actions) => {
        trackUpdates(values, agentInitialValues, currentTabTitle);
        setFormikValues(values);
        const {
          billingTier,
          minutesPlan,
          localP,
          ftcBlocking,
        } = values.accountSettings;
        const currentBillingTier =
          billingTiers[
            myBusinessUsersCached.current?.nextUserTier ||
              (myBusinessUsersCached.current?.userTier as string)
          ]?.tier ?? '';

        const currentMinutesPlan =
          billingTiers[
            myBusinessUsersCached.current?.nextUserTier ||
              (myBusinessUsersCached.current?.userTier as string)
          ]?.unlimited ?? '';

        const currentPowerlistProduct =
          myBusinessUsersCached.current?.nextPowerlistProductId ||
          (myBusinessUsersCached.current?.powerlistEndDate
            ? 0
            : myBusinessUsersCached.current?.powerlistProductId || 0);
        const billingTierChange = currentBillingTier !== billingTier;
        const minutesPlanChange = currentMinutesPlan !== minutesPlan;
        const powerlistProductChange =
          currentPowerlistProduct !== values?.manage?.powerlistProduct;
        const connectionBoostChange =
          localP !== (myBusinessUsersCached.current?.localPresenceLite === 1);
        const DNCAlgorithmChange =
          ftcBlocking !== (myBusinessUsersCached.current?.ftcBlocking === 1);
        const updatePdCallTranscriptionProduct =
          values.manage.hasPdCallTranscription !==
          Boolean(myBusinessUsersCached.current?.hasPdCallTranscription);

        let bulkProductChangeToUpdate: {
          userId: number;
          product: string;
          productFamily?: string;
        }[] = [];

        if (billingTierChange || minutesPlanChange) {
          let tierName = '';

          if (minutesPlan === 1) {
            if (billingTier === 1) {
              tierName = 'Enterprise Unlimited';
            } else if (billingTier === 2) {
              tierName = 'Integrated Unlimited';
            } else if (billingTier === 3) {
              tierName = 'Basic Unlimited';
            }
          } else if (minutesPlan === 0) {
            if (billingTier === 1) {
              tierName = 'Enterprise';
            } else if (billingTier === 2) {
              tierName = 'Integrated';
            } else if (billingTier === 3) {
              tierName = 'Basic';
            }
          }
          if (billingTier === 4) {
            tierName = 'payperuse';
          }
          const productName = products.find(
            (product) => product?.tier === tierName
          )?.product;
          bulkProductChangeToUpdate = [
            ...bulkProductChangeToUpdate,
            {
              userId: myBusinessUsersCached.current?.userid,
              product: productName,
              productFamily: 'phone',
            },
          ] as any;
        }
        const activeUserProductsToUpdate = activeUserProducts?.result?.length
          ? [...(activeUserProducts?.result ?? [])]
          : [];

        if (powerlistProductChange) {
          const selectedPowerlistProduct = powerlistDialerProductMap?.find(
            (el) => el?.id === values?.manage?.powerlistProduct
          );

          // if the user has selected none for powerlistProduct
          if (selectedPowerlistProduct?.id === 0) {
            // if the userProducts has a power list product remove it
            activeUserProductsToUpdate?.forEach((product, i) => {
              if (product?.productFamily === 'powerlistDialer') {
                activeUserProductsToUpdate.splice(i, 1);
              }
            });
          } else {
            activeUserProductsToUpdate?.forEach((product, i) => {
              if (product?.productFamily === 'powerlistDialer') {
                activeUserProductsToUpdate.splice(i, 1);
              }
            });
            bulkProductChangeToUpdate = [
              ...bulkProductChangeToUpdate,
              {
                userId: myBusinessUsersCached.current?.userid,
                product: selectedPowerlistProduct?.product,
                productFamily: 'powerlistDialer',
              },
            ] as any;
          }
        }
        if (connectionBoostChange) {
          if (localP) {
            bulkProductChangeToUpdate = [
              ...bulkProductChangeToUpdate,
              {
                userId: myBusinessUsersCached.current?.userid,
                product: 'localpresencelite',
                productFamily: 'localPresenceLite',
              },
            ] as any;
          } else {
            // remove connectionBoostChange
            let indexOfLocalP = -1;
            activeUserProductsToUpdate?.forEach((product, i) => {
              if (product?.productFamily === 'localPresenceLite') {
                indexOfLocalP = i;
              }
            });
            if (indexOfLocalP !== -1) {
              activeUserProductsToUpdate.splice(indexOfLocalP, 1);
            }
          }
        }
        if (DNCAlgorithmChange) {
          if (ftcBlocking) {
            bulkProductChangeToUpdate = [
              ...bulkProductChangeToUpdate,
              {
                userId: myBusinessUsersCached.current?.userid,
                product: 'ftcblocking',
                productFamily: 'ftcBlocking',
              },
            ] as any;
          } else {
            // remove ftcBlocking
            let indexOfFtcBlocking = -1;
            activeUserProductsToUpdate?.forEach((product, i) => {
              if (product?.productFamily === 'ftcBlocking') {
                indexOfFtcBlocking = i;
              }
            });
            if (indexOfFtcBlocking !== -1) {
              activeUserProductsToUpdate.splice(indexOfFtcBlocking, 1);
            }
          }
        }
        if (updatePdCallTranscriptionProduct) {
          if (values.manage.hasPdCallTranscription) {
            bulkProductChangeToUpdate = [
              ...bulkProductChangeToUpdate,
              {
                userId: myBusinessUsersCached.current?.userid,
                product: 'calltranscription',
                productFamily: 'conversationIntelligence',
              },
            ] as any;
          } else {
            // remove calltranscription
            let indexOfCalltranscription = -1;
            activeUserProductsToUpdate?.forEach((product, i) => {
              if (product?.productFamily === 'conversationIntelligence') {
                indexOfCalltranscription = i;
              }
            });
            if (indexOfCalltranscription !== -1) {
              activeUserProductsToUpdate.splice(indexOfCalltranscription, 1);
            }
          }
        }

        const totalProductsToUpdate = [...bulkProductChangeToUpdate];

        const updatedFamilies = bulkProductChangeToUpdate?.map(
          (product) => (product as any)?.productFamily
        );

        activeUserProductsToUpdate?.forEach((product, i) => {
          if (updatedFamilies?.indexOf(product?.productFamily) === -1) {
            totalProductsToUpdate.push({
              userId: product?.userid,
              product: product?.product,
            });
          }
        });

        const updated = totalProductsToUpdate.map((product) => ({
          userId: product.userId,
          product: product?.product,
        }));
        setUserProductChanges(updated);
        const showManageBulkProducts =
          billingTierChange ||
          minutesPlanChange ||
          powerlistProductChange ||
          connectionBoostChange ||
          DNCAlgorithmChange ||
          updatePdCallTranscriptionProduct;
        if (showManageBulkProducts && !inTrial) {
          setShowProductChanges(true);
        } else {
          submitForm(false, values);
        }
      }}
      enableReinitialize={true}
      initialValues={agentInitialValues}
    >
      {(formik) => {
        function handleSubmit() {
          if (!createHoursString(schedulableHoursCached.current)) {
            enqueueSnackbarWithStyles({
              message: 'Please select a time range',
              alertProps: {
                severity: 'warning',
              },
            });
          } else {
            formik.setFieldValue(
              'accountSettings.userHours',
              schedulableHoursCached.current
            );
            onHideHours();
          }
        }

        const userHoursString = useMemo(
          () =>
            createHoursString(
              formik.values.accountSettings?.useBusinessHoursEnabled
                ? businessCached.current?.businessDetails?.businessHours
                : formik.values.accountSettings?.userHours
            ).split(','),
          [
            showBusinessHours,
            setShowBusinessHours,
            formik.values.accountSettings?.useBusinessHoursEnabled,
            formik.values.accountSettings?.userHours,
            businessLoadable,
          ]
        );
        return (
          <>
            <form
              className="form form-label-right"
              onSubmit={() => {
                trackSaveEditAgent();
                formik.handleSubmit();
              }}
              noValidate
            >
              <TabContext value={String(tabIndex)}>
                <WizardModal
                  title={tabNames?.[tabIndex]?.title ?? ''}
                  disableSubmit={!formik.dirty}
                  size="lg"
                  show={show}
                  onHide={onHide}
                  onSubmit={formik.handleSubmit}
                  isLoading={
                    formik.isSubmitting ||
                    submitState === loadableEnum.LOADING ||
                    myBusinessUsersLoadable?.state === 'loading' ||
                    businessNumbersLoadable.state === 'loading' ||
                    getActiveUserProductsLoading ||
                    productChangesLoading
                  }
                  navigation={
                    <>
                      <Tab
                        label="Edit Agent Settings"
                        disabled={true}
                        className={clsx(classes.tab)}
                      />
                      <TabList
                        onChange={handleChange(formik)}
                        aria-label="Edit Agent Settings"
                        orientation="vertical"
                        variant="standard"
                        className={clsx(classes.tabs, {})}
                      >
                        {tabNames.map((tabItem, index) => (
                          <Tab
                            {...{ name: tabItem.title }}
                            key={index}
                            className={classes.tab}
                            data-pup={tabItem.dataPup}
                            label={
                              <TabLabel
                                index={index}
                                formik={formik}
                                tabToFields={tabToFields}
                                tabItem={tabItem}
                              />
                            }
                            value={String(index)}
                          />
                        ))}
                      </TabList>
                    </>
                  }
                  dialogActions={
                    <div className="container">
                      <div>
                        <div
                          className="row"
                          style={{ justifyContent: 'flex-end' }}
                        >
                          {(role === Role.SUPERADMIN ||
                            role === Role.ADMIN ||
                            role === Role.MANAGER) &&
                            permissionMemo && (
                              <Button
                                className={clsx(
                                  classes.button,
                                  classes.buttonContainedRed
                                )}
                                disableElevation
                                variant="contained"
                                disabled={formik.isSubmitting}
                                onClick={() => {
                                  handleShow();
                                }}
                              >
                                Delete Agent
                              </Button>
                            )}
                          <span style={{ flexGrow: 1 }}></span>
                          <Button
                            className={classes.button}
                            disableElevation
                            variant="contained"
                            color="inherit"
                            disabled={formik.isSubmitting}
                            onClick={() => {
                              onHide();
                            }}
                            data-pup="edit-agent-cancel"
                          >
                            Cancel
                          </Button>
                          <Button
                            className={classes.button}
                            disableElevation
                            variant="contained"
                            color="primary"
                            disabled={formik.isSubmitting || !formik.dirty}
                            data-pup="edit-agent-save"
                            // disabled={
                            //   formik.isSubmitting ||
                            //   !formik.dirty ||
                            //   !formik.isValid
                            // }
                            onClick={(event) => {
                              // trigger show errors if errors exists
                              formik.handleSubmit(event as any);
                              // switch tabs and suppress submit if error not in current tab (submit will block if errors detected)
                              if (
                                !formik.isValid && // if form invalid
                                !isErrorInTab(
                                  tabIndex,
                                  formik.errors,
                                  tabToFields
                                ) // and error not in this tab
                              ) {
                                const errorTabIndex = getErrorTabIndex(
                                  formik.errors,
                                  tabToFields
                                );
                                /**
                                 * switch to tab containing error
                                 *
                                 * since form validation is in flight and makes page rendering updates
                                 * set state change in separate event loop cycle (tick) since it also makes page rendering updates
                                 * putting state changes in a separate tick will prevent coliding page rendering updates
                                 *
                                 * @see https://blog.sessionstack.com/how-javascript-works-event-loop-and-the-rise-of-async-programming-5-ways-to-better-coding-with-2f077c4438b5
                                 */
                                setTimeout(() =>
                                  setTabIndex(errorTabIndex ?? 0)
                                );
                              }
                            }}
                          >
                            Save
                          </Button>
                          {(role === Role.SUPERADMIN ||
                            role === Role.ADMIN ||
                            role === Role.MANAGER) && (
                            <Button
                              className={classes.button}
                              disableElevation
                              variant="contained"
                              color="primary"
                              disabled={
                                formik.isSubmitting ||
                                !formik.dirty ||
                                !formik.isValid ||
                                formik.values.accountSettings.email ===
                                  myBusinessUsersCached.current?.email
                              }
                              onClick={() => {
                                handleShowR();
                              }}
                            >
                              Save & Replace Agent
                            </Button>
                          )}
                        </div>
                        {/* {(role === Role.SUPERADMIN ||
                        role === Role.ADMIN ||
                        role === Role.MANAGER) && (
                        <div className={clsx('row', classes.helpText)}>
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://kixie.zendesk.com/hc/en-us/articles/227010668-Deleting-Replacing-Users"
                          >
                            Should I Delete or Replace?
                          </a>
                        </div>
                      )} */}
                      </div>
                    </div>
                  }
                >
                  <TabPanel value="0">
                    <FormikTextField
                      name="accountSettings.fname"
                      className={classes.field}
                      formik={formik}
                      fullWidth
                      label="First Name"
                      size="small"
                      required
                      variant="outlined"
                    />
                    <FormikTextField
                      name="accountSettings.lname"
                      className={classes.field}
                      formik={formik}
                      fullWidth
                      label="Last Name"
                      size="small"
                      required
                      variant="outlined"
                    />
                    <FormikTextField
                      name="accountSettings.callerIdName"
                      className={classes.field}
                      formik={formik}
                      fullWidth
                      label="Outbound Caller ID Name"
                      size="small"
                      required
                      variant="outlined"
                      inputProps={{ maxLength: 60 }}
                    />
                    <MaterialAutocomplete
                      className={classes.field}
                      type="text"
                      name="accountSettings.outboundCallerId"
                      fullWidth
                      formik={formik}
                      defaultValue=""
                      label="Outbound Caller ID Number"
                      size="small"
                      variant="outlined"
                      multiple={false}
                      options={businessNumbersCached.current.agentNumbers
                        .sort()
                        .concat(businessNumbersCached.current.ivrNumbers.sort())
                        .concat(
                          businessNumbersCached.current.groupNumbers.sort()
                        )
                        .concat(
                          businessNumbersCached.current.queueNumbers.sort()
                        )}
                      required
                      renderOption={(props, option) => (
                        <li {...props}>
                          {`${option} ${
                            (businessNumbersCached.current?.lookupNumbers[
                              option
                            ] as any)?.type
                          }: ${
                            (businessNumbersCached.current?.lookupNumbers[
                              option
                            ] as any)?.destinationName
                          }`}
                        </li>
                      )}
                      filterOptions={filterOptions}
                    />
                    <MaterialAutocomplete
                      className={classes.field}
                      type="text"
                      fullWidth
                      name="accountSettings.outboundSmsNumber"
                      formik={formik}
                      label="Outbound SMS Number"
                      size="small"
                      variant="outlined"
                      multiple={false}
                      defaultValue=""
                      options={businessNumbersCached.current.myNumbers}
                      required
                      renderOption={(props, option) => (
                        <li {...props}>
                          {`${option} ${
                            (businessNumbersCached.current?.lookupNumbers[
                              option
                            ] as any)?.type
                          }`}
                        </li>
                      )}
                    />
                    {!businessNumbersCached.current.myNumbers.length &&
                      role !== Role.USER && (
                        <Link
                          href={`/manage/agents/${
                            (agentUserIdAtom.contents as any).id
                          }/numbers`}
                        >
                          {`Buy An SMSable Number`}
                        </Link>
                      )}
                    <FormikTextField
                      name="accountSettings.email"
                      className={classes.field}
                      formik={formik}
                      fullWidth
                      label="Email"
                      helperText="To Save & Replace an Agent please update the email."
                      size="small"
                      type="email"
                      required
                      variant="outlined"
                    />
                  </TabPanel>
                  <TabPanel value="1">
                    <div style={{ marginTop: '16px' }}>
                      <LabelledOutline label="User Hours">
                        <div className="row">
                          <div className="col-lg-6">
                            Actions will be routed:
                            {userHoursString.map((string, index) => (
                              <div key={index}>
                                {string} <br />
                              </div>
                            ))}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              // paddingRight: '50px',
                              alignItems: 'center',
                            }}
                            className="col-lg-6"
                          >
                            <FormControlLabel
                              control={
                                <FormikSwitch
                                  name="accountSettings.useBusinessHoursEnabled"
                                  formik={formik}
                                  color="primary"
                                  type="checkbox"
                                />
                              }
                              label="Use Business Hours"
                            />
                            <Button
                              variant="contained"
                              color="primary"
                              className={classes.editButton}
                              onClick={handleShowScheduler}
                              disabled={
                                formik.values.accountSettings
                                  ?.useBusinessHoursEnabled
                              }
                            >
                              Edit User Hours
                            </Button>
                          </div>
                        </div>
                      </LabelledOutline>
                      <DefaultAction
                        defaultAction={getDefaultActionUrl({
                          type:
                            formik.values.accountSettings?.closedActionPayload
                              ?.type,
                          parentType: 'user',
                          businessid: userAtomContents?.businessid,
                          user:
                            formik.values.accountSettings?.closedActionPayload
                              ?.user,
                          group:
                            formik.values.accountSettings?.closedActionPayload
                              ?.group,
                          ivr:
                            formik.values.accountSettings?.closedActionPayload
                              ?.ivr,
                          queue:
                            formik.values.accountSettings?.closedActionPayload
                              ?.queue,
                          voicemail: id,
                        })}
                        label="Outside User Hours Action"
                        labelForBusiness="Outside Business Hours Action"
                        actionType={
                          formik.values.accountSettings?.closedActionPayload
                            ?.type
                        }
                        typeName="accountSettings.closedActionPayload.type"
                        ivrName="accountSettings.closedActionPayload.ivr"
                        groupName="accountSettings.closedActionPayload.group"
                        userName="accountSettings.closedActionPayload.user"
                        queueName="accountSettings.closedActionPayload.queue"
                        formik={formik}
                        disableFormUpdate={true}
                        id={id}
                      />
                    </div>
                    <ModalWithStyles
                      onSubmit={handleSubmit}
                      title={'Business Hours'}
                      show={showScheduler}
                      onHide={onHideHours}
                      isSubmitting={formik.isSubmitting}
                      size="lg"
                    >
                      <Scheduler
                        schedulableHours={
                          formik.values.accountSettings?.userHours
                        }
                        setSchedulableHours={handleSetSchedulableHours}
                      />
                    </ModalWithStyles>
                  </TabPanel>
                  <TabPanel
                    value="2"
                    style={{ display: role !== Role.USER ? '' : 'none' }}
                  >
                    {myBusinessUsersCached.current?.userTier !==
                      myBusinessUsersCached.current?.nextUserTier &&
                    myBusinessUsersCached.current?.nextUserTierStartDate &&
                    role !== Role.USER ? (
                      <p className={classes.downgradeMessage}>
                        Change from&nbsp;
                        <b>
                          {myBusinessUsersCached.current.userTierDisplayName}
                        </b>
                        &nbsp;to&nbsp;
                        <b>
                          {
                            myBusinessUsersCached.current
                              .nextUserTierDisplayName
                          }
                        </b>
                        &nbsp;takes effect on&nbsp;
                        <b>
                          {DateTime.fromISO(
                            myBusinessUsersCached.current.nextUserTierStartDate
                          ).toLocaleString(DateTime.DATETIME_SHORT)}
                        </b>
                      </p>
                    ) : (
                      ''
                    )}
                    {myBusinessUsersCached.current?.powerlistProductId !==
                      myBusinessUsersCached.current?.nextPowerlistProductId &&
                    myBusinessUsersCached.current?.nextPowerlistStartDate &&
                    role !== Role.USER ? (
                      <p className={classes.downgradeMessage}>
                        Change from&nbsp;
                        <b>
                          {myBusinessUsersCached.current.powerlistDisplayName}
                        </b>
                        &nbsp;to&nbsp;
                        <b>
                          {
                            myBusinessUsersCached.current
                              .nextPowerlistDisplayName
                          }
                        </b>
                        &nbsp;takes effect on&nbsp;
                        <b>
                          {DateTime.fromISO(
                            myBusinessUsersCached.current.nextPowerlistStartDate
                          ).toLocaleString(DateTime.DATETIME_SHORT)}
                        </b>
                      </p>
                    ) : myBusinessUsersCached.current?.powerlistEndDate &&
                      role !== Role.USER ? (
                      <p className={classes.downgradeMessage}>
                        <b>
                          {myBusinessUsersCached.current.powerlistDisplayName}
                        </b>
                        &nbsp;ends on&nbsp;
                        <b>
                          {DateTime.fromISO(
                            myBusinessUsersCached.current.powerlistEndDate
                          ).toLocaleString(DateTime.DATETIME_SHORT)}
                        </b>
                      </p>
                    ) : (
                      ''
                    )}
                    {role !== Role.USER && (
                      <div className="row borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">Billing Tier</h5>
                          <p className="fieldDescription">
                            The popular{' '}
                            <Link
                              href={`https://www.kixie.com/pricing/`}
                              underline="hover"
                              target="_blank"
                            >
                              Professional tier
                            </Link>{' '}
                            allows access to Voicemail Drops, SMS Templates,
                            automations, and additional add-ons like PowerDialer
                            and ConnectionBoost. The Integrated tier provides
                            basic calling and texting functionality with CRM
                            integration.
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormikSelect
                            name="accountSettings.billingTier"
                            className={classes.field}
                            formik={formik}
                            fullWidth
                            label="Billing Tier"
                            margin="dense"
                            size="small"
                            variant="outlined"
                            required
                            onChange={(event) => {
                              // show billing tier upgrade to enterprise message
                              if (
                                // if not enterprise
                                event.target.value != 1 &&
                                // if not none
                                formik.values.manage.powerlistProduct !== 0
                              ) {
                                setShowDowngrade(true);
                              }
                              if (event.target.value != 1) {
                                // Reset Connection Boost when downgrading below Professional so that the change persists on save
                                formik.setFieldValue('accountSettings.localP', false);
                              }
                              if (event.target.value === 4) {
                                formik.setFieldValue(
                                  'accountSettings.minutesPlan',
                                  0
                                );
                                formik.setFieldValue(
                                  'accountSettings.ftcBlocking',
                                  0
                                );
                                formik.setFieldValue(
                                  'manage.hasPdCallTranscription',
                                  0
                                );
                              }
                            }}
                          >
                            <MenuItem
                              disabled={role !== Role.SUPERADMIN}
                              value={3}
                            >
                              Basic
                            </MenuItem>
                            <MenuItem value={2}>Integrated</MenuItem>
                            <MenuItem value={1}>Professional</MenuItem>
                            {
                              // ENG-5254: Hide "Free" Billing Tier unless user is already on Free billing
                              (enableFreeBillingTier ||
                                formik.values.accountSettings.billingTier.toString() ===
                                  '4') && (
                                <MenuItem
                                  disabled={role !== Role.SUPERADMIN}
                                  value={4}
                                >
                                  Free
                                </MenuItem>
                              )
                            }
                          </FormikSelect>
                        </div>
                      </div>
                    )}
                    {role !== Role.USER && (
                      <div className="row borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">Minutes Plan</h5>
                          <p className="fieldDescription">
                            If an agent will be making more than 1 hour of
                            outbound calls per business day to USA or Canada,
                            choose the Unlimited plan to save money. Otherwise,
                            choose the Per Minute plan. SMS messages are charged
                            per message,{' '}
                            <Link
                              href={`https://support.kixie.com/hc/en-us/articles/225116528-How-does-Kixie-s-billing-work`}
                              underline="hover"
                              target="_blank"
                            >
                              regardless of plan
                            </Link>
                            .
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormikSelect
                            name="accountSettings.minutesPlan"
                            className={classes.field}
                            formik={formik}
                            fullWidth
                            label="Minutes Plan"
                            margin="dense"
                            size="small"
                            variant="outlined"
                            required
                            disabled={
                              formik.values.accountSettings.billingTier.toString() ===
                              '4'
                            }
                          >
                            <MenuItem value={1}>Unlimited</MenuItem>
                            <MenuItem value={0}>Per Minute</MenuItem>
                          </FormikSelect>
                        </div>
                      </div>
                    )}
                    {role !== Role.USER && (
                      <div className="row borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">PowerDialer Product</h5>
                          <p className="fieldDescription">
                            Standard PowerDialer enables the agent to dial up to
                            1, 4 or 10 numbers at a time based on the chosen
                            tier. The AI Human Detect tier skips identified
                            voicemails and phone trees, so the agent can connect
                            with live human answers instead. Ensure agents are
                            always calling the hottest leads first with{' '}
                            <Link
                              href={`https://support.kixie.com/hc/en-us/articles/20404809914523-Prioritized-PowerLists`}
                              underline="hover"
                              target="_blank"
                            >
                              Prioritized PowerLists
                            </Link>
                            .
                          </p>
                        </div>
                        {canAccessPowerlistProduct && (
                          <div className="col-lg-4">
                            <FormikSelect
                              name="manage.powerlistProduct"
                              className={classes.field}
                              formik={formik}
                              fullWidth
                              label="PowerDialer Product"
                              margin="dense"
                              size="small"
                              variant="outlined"
                              required
                              data-pup="edit-agent-powerlist-product"
                              disabled={
                                formik.values.accountSettings.billingTier.toString() ===
                                '4'
                              }
                              onChange={(event) => {
                                // show billing tier upgrade to enterprise message
                                if (
                                  // if not enterprise
                                  formik.values.accountSettings.billingTier !=
                                    1 &&
                                  // if not none
                                  event.target.value !== 0
                                ) {
                                  setShowUpgrade(true);
                                }
                              }}
                            >
                              <MenuItem
                                value={0}
                                data-pup="edit-agent-powerlist-none"
                              >
                                None
                              </MenuItem>
                              <MenuItem
                                value={30}
                                data-pup="edit-agent-powerlist-one"
                              >
                                PowerDialer One
                              </MenuItem>
                              <MenuItem
                                value={31}
                                data-pup="edit-agent-powerlist-four"
                              >
                                PowerDialer Four
                              </MenuItem>
                              <MenuItem
                                value={32}
                                data-pup="edit-agent-powerlist-ten"
                              >
                                PowerDialer Ten
                              </MenuItem>
                              {isMachineDetectionFeatureEnabled && (
                                <MenuItem
                                  value={POWERDIALER_MACHINE_DETECT_PRODUCT_ID}
                                  data-pup="edit-agent-powerlist-machine-detect"
                                >
                                  <div
                                    style={{ display: 'flex', width: '100%' }}
                                  >
                                    <span style={{ flexGrow: 1 }}>
                                      PowerDialer AI Human Detect
                                    </span>
                                    <span></span>
                                  </div>
                                </MenuItem>
                              )}
                            </FormikSelect>
                          </div>
                        )}
                      </div>
                    )}
                    {role !== Role.USER && (
                      <div>
                        <div className="row borderBottom">
                          <div className="col-lg-8">
                            <h5 className="fieldTitle">Connection Boost</h5>
                            <p className="fieldDescription">
                              <Link
                                href={`https://support.kixie.com/hc/en-us/articles/360057490914-What-is-Kixie-ConnectionBoost`}
                                underline="hover"
                                target="_blank"
                              >
                                Improve outbound connection rates
                              </Link>{' '}
                              with local presence dialing, progressive caller ID
                              and algorithms that minimize the chance of showing
                              as spam across a large pool of numbers. Callbacks
                              route back to the agent who made the call.
                            </p>
                          </div>
                          <div
                            style={{ paddingLeft: '12px' }}
                            className="col-lg-4"
                          >
                            <FormControlLabel
                              control={
                                <FormikSwitch
                                  name="accountSettings.localP"
                                  formik={formik}
                                  color="primary"
                                  type="checkbox"
                                  disabled={
                                    formik.values.accountSettings.billingTier.toString() !==
                                    '1'
                                  }
                                />
                              }
                              label="Connection Boost &nbsp;&nbsp;"
                            />
                          </div>
                        </div>
                        {process.env.VITE_APP_FEATURE_FTC_BLOCKING === '1' && (
                          <div className="row borderBottom">
                            <div className="col-lg-8">
                              <h5 className="fieldTitle">DNC Algorithm</h5>
                              <p className="fieldDescription">
                                The agent will be blocked from making a call or
                                sending an SMS to a phone number that Kixie's
                                proprietary{' '}
                                <Link
                                  href={`https://support.kixie.com/hc/en-us/articles/16376376568859-DNC-Algorithm-for-Calls-SMS`}
                                  underline="hover"
                                  target="_blank"
                                >
                                  DNC algorithm
                                </Link>{' '}
                                detects as likely to be included on the Do Not
                                Call Registry.
                              </p>
                            </div>
                            <div
                              style={{ paddingLeft: '12px' }}
                              className="col-lg-4"
                            >
                              <FormControlLabel
                                control={
                                  <FormikSwitch
                                    name="accountSettings.ftcBlocking"
                                    formik={formik}
                                    color="primary"
                                    type="checkbox"
                                  />
                                }
                                disabled={
                                  formik.values.accountSettings.billingTier.toString() ===
                                  '4'
                                }
                                label="DNC Algorithm &nbsp;&nbsp;"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {role !== Role.USER && (
                      <div className="row">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">
                            Conversation Intelligence
                          </h5>
                          <p className="fieldDescription">
                            Transcribes and analyzes the agent's call
                            recordings, making it easier for managers to review
                            the agent's calls.
                          </p>
                        </div>
                        <div className="col-lg-4">
                          {hasCallTranscriptionFeaturedEnabled &&
                            (role === Role.SUPERADMIN ||
                              role === Role.ADMIN ||
                              role === Role.MANAGER) && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'baseline',
                                  justifyContent: 'space-between',
                                  marginBottom: '8px',
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <FormikSwitch
                                      name="manage.hasPdCallTranscription"
                                      formik={formik}
                                      color="primary"
                                      type="checkbox"
                                    />
                                  }
                                  disabled={
                                    formik.values.accountSettings.billingTier.toString() ===
                                    '4'
                                  }
                                  label="Conversation Intelligence &nbsp;&nbsp;"
                                />
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </TabPanel>
                  <TabPanel value={role !== Role.USER ? '3' : '2'}>
                    {myBusinessUsersCached.current?.userTier !==
                      myBusinessUsersCached.current?.nextUserTier &&
                    myBusinessUsersCached.current?.nextUserTierStartDate &&
                    role !== Role.USER ? (
                      <p className={classes.downgradeMessage}>
                        Change from&nbsp;
                        <b>
                          {myBusinessUsersCached.current.userTierDisplayName}
                        </b>
                        &nbsp;to&nbsp;
                        <b>
                          {
                            myBusinessUsersCached.current
                              .nextUserTierDisplayName
                          }
                        </b>
                        &nbsp;takes effect on&nbsp;
                        <b>
                          {DateTime.fromISO(
                            myBusinessUsersCached.current.nextUserTierStartDate
                          ).toLocaleString(DateTime.DATETIME_SHORT)}
                        </b>
                      </p>
                    ) : (
                      ''
                    )}
                    {myBusinessUsersCached.current?.powerlistProductId !==
                      myBusinessUsersCached.current?.nextPowerlistProductId &&
                    myBusinessUsersCached.current?.nextPowerlistStartDate &&
                    role !== Role.USER ? (
                      <p className={classes.downgradeMessage}>
                        Change from&nbsp;
                        <b>
                          {myBusinessUsersCached.current.powerlistDisplayName}
                        </b>
                        &nbsp;to&nbsp;
                        <b>
                          {
                            myBusinessUsersCached.current
                              .nextPowerlistDisplayName
                          }
                        </b>
                        &nbsp;takes effect on&nbsp;
                        <b>
                          {DateTime.fromISO(
                            myBusinessUsersCached.current.nextPowerlistStartDate
                          ).toLocaleString(DateTime.DATETIME_SHORT)}
                        </b>
                      </p>
                    ) : myBusinessUsersCached.current?.powerlistEndDate &&
                      role !== Role.USER ? (
                      <p className={classes.downgradeMessage}>
                        <b>
                          {myBusinessUsersCached.current.powerlistDisplayName}
                        </b>
                        &nbsp;ends on&nbsp;
                        <b>
                          {DateTime.fromISO(
                            myBusinessUsersCached.current.powerlistEndDate
                          ).toLocaleString(DateTime.DATETIME_SHORT)}
                        </b>
                      </p>
                    ) : (
                      ''
                    )}
                    {role !== Role.USER && (
                      <div className="row borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">User Type</h5>
                          <p className="fieldDescription">
                            Admins and Managers have access to managing the
                            overall account's settings, activity history, call
                            coaching, reporting, and more. Users have access
                            only to reporting by default and can only view their
                            own activity history.
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormikSelect
                            name="accountSettings.userType"
                            className={classes.field}
                            formik={formik}
                            fullWidth
                            disabled={disableUserType}
                            label="User Type"
                            margin="dense"
                            size="small"
                            variant="outlined"
                            required
                          >
                            {roleDropdown
                              .filter((dropdown) => !dropdown.hide)
                              .map((dropdown, index) => (
                                <MenuItem
                                  key={index}
                                  disabled={dropdown.disabled}
                                  value={dropdown.role}
                                >
                                  <span style={{ textTransform: 'capitalize' }}>
                                    {dropdown.role}
                                  </span>
                                </MenuItem>
                              ))}
                          </FormikSelect>
                        </div>
                      </div>
                    )}
                    {role !== Role.USER && (
                      <div className="row borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">User Extension</h5>
                          <p className="fieldDescription">
                            The agent can be dialed by this extension without
                            having to dial their phone number.
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormikTextField
                            name="settings.userExt"
                            className={classes.field}
                            formik={formik}
                            fullWidth
                            label="User Extension"
                            margin="dense"
                            size="small"
                            type="number"
                            required
                            variant="outlined"
                          />
                        </div>
                      </div>
                    )}
                    <div
                      className={`row ${
                        role !== Role.USER ? 'borderBottom' : ''
                      }`}
                    >
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">Voicemail Drop</h5>
                        <p className="fieldDescription">
                          Select an option for the desired{' '}
                          <Link
                            href={`https://support.kixie.com/hc/en-us/articles/4576581944731-Setting-up-and-using-Voicemail-Drop`}
                            underline="hover"
                            target="_blank"
                          >
                            Voicemail Drop
                          </Link>{' '}
                          method. Silence waits to detect a period of silence,
                          and should be dropped during the voicemail greeting.
                          We recommend this option for best results. Beep waits
                          to detect a beep after the voicemail greeting, and
                          should also be dropped during the voicemail greeting.
                          Manual allows the agent to drop with 100% accuracy,
                          but requires waiting until the voicemail greeting is
                          finished.
                        </p>
                      </div>
                      <div className="col-lg-4">
                        <FormikSelect
                          name="accountSettings.vmDrop"
                          className={classes.field}
                          formik={formik}
                          fullWidth
                          label="Voicemail Drop"
                          margin="dense"
                          size="small"
                          variant="outlined"
                          required
                        >
                          <MenuItem value="beep">Beep</MenuItem>
                          <MenuItem value="silence">Silence</MenuItem>
                          <MenuItem value="manual">Manual</MenuItem>
                        </FormikSelect>
                      </div>
                    </div>
                    {role === Role.SUPERADMIN &&
                      [995, 21332].includes(
                        userAtomContents?.businessid ?? null
                      ) &&
                      process.env.VITE_APP_AGENT_QUEUE === '1' && (
                        <div className="row superStyle borderBottom">
                          <div className="col-lg-8">
                            <h5 className="fieldTitle">Agent Queue</h5>
                            <p className="fieldDescription">
                              Turn on Agent Queue to allow multiple subsequent
                              calls to an agent to wait in line to be answered,
                              instead of resulting in a no-answer action.
                            </p>
                          </div>
                          <div className="col-lg-4">
                            <FormControlLabel
                              control={
                                <FormikSwitch
                                  name="accountSettings.agentQueueEnabled"
                                  formik={formik}
                                  color="primary"
                                  type="checkbox"
                                />
                              }
                              label="Agent Queue"
                            />
                          </div>
                        </div>
                      )}
                    {role === Role.SUPERADMIN && (
                      <div className="row superStyle borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">Error Logging</h5>
                        </div>
                        <div className="col-lg-4">
                          <FormControlLabel
                            control={
                              <FormikSwitch
                                name="accountSettings.logLevelEnabled"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                              />
                            }
                            label="Error Logging &nbsp;&nbsp;"
                          />
                        </div>
                      </div>
                    )}
                    {role !== Role.USER && (
                      <div className="row">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">Override DNC</h5>
                          <p className="fieldDescription">
                            Toggle this on to allow outbound calls to numbers
                            detected by Kixie's DNC Algorithm as likely to be on
                            the Do Not Call Registry. Use this in the event
                            there are connections with numbers on that list who
                            have explicitly requested calls from you.
                            <span
                              style={{
                                display: 'block',
                              }}
                            >
                              Note: This setting does not apply to PowerList
                              calls.
                            </span>
                          </p>
                        </div>
                        <div className="col-lg-4">
                          {process.env.VITE_APP_AGENT_OVERRIDE_DNC === '1' &&
                            (role === Role.SUPERADMIN ||
                              role === Role.MANAGER ||
                              role === Role.ADMIN) && (
                              <FormControlLabel
                                control={
                                  <FormikSwitch
                                    name="accountSettings.overrideDnc"
                                    formik={formik}
                                    color="primary"
                                    type="checkbox"
                                  />
                                }
                                label="Override DNC &nbsp;&nbsp;"
                              />
                            )}
                        </div>
                      </div>
                    )}
                  </TabPanel>
                  <TabPanel value={role !== Role.USER ? '4' : '3'}>
                    {role !== Role.USER && (
                      <div className="row borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">Show Call Recordings</h5>
                          <p className="fieldDescription">
                            Show agent's call recordings in Call History
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormControlLabel
                            control={
                              <FormikSwitch
                                name="accountSettings.othersCanViewRec"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                              />
                            }
                            label="Show Call Recordings"
                          />
                        </div>
                      </div>
                    )}
                    {role !== Role.USER && (
                      <div className="row borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">Record Calls</h5>
                          <p className="fieldDescription">
                            Record all calls for agent
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormControlLabel
                            control={
                              <FormikSwitch
                                name="accountSettings.recordCalls"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                              />
                            }
                            label="Record Calls"
                          />
                        </div>
                      </div>
                    )}
                    {hasSetSuperadminPermissions &&
                      myBusinessUsersCached?.current?.type ===
                        Role.SUPERADMIN && (
                        <div>
                          {canViewSetSuperadminPermissions && (
                            <div className="row superStyle borderBottom">
                              <div className="col-lg-8">
                                <h5 className="fieldTitle">
                                  Set Superadmin Permissions
                                </h5>
                              </div>
                              <div className="col-lg-4">
                                {role === Role.SUPERADMIN && (
                                  <FormControlLabel
                                    control={
                                      <FormikSwitch
                                        name="accountSettings.setSuperadminPermissions"
                                        formik={formik}
                                        color="primary"
                                        type="checkbox"
                                      />
                                    }
                                    label="Set Superadmin Permissions &nbsp;&nbsp;"
                                  />
                                )}
                              </div>
                            </div>
                          )}
                          <div className="row superStyle borderBottom">
                            <div className="col-lg-8">
                              <h5 className="fieldTitle">Switch User</h5>
                            </div>
                            <div className="col-lg-4">
                              {role === Role.SUPERADMIN && (
                                <FormControlLabel
                                  control={
                                    <FormikSwitch
                                      name="accountSettings.switchUser"
                                      formik={formik}
                                      color="primary"
                                      type="checkbox"
                                    />
                                  }
                                  label="Switch User &nbsp;&nbsp;"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    <div className="row borderBottom">
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">Enable Call Waiting</h5>
                        <p className="fieldDescription">
                          Agent will receive incoming calls while on another
                          call
                        </p>
                      </div>
                      <div className="col-lg-4">
                        <FormControlLabel
                          control={
                            <FormikSwitch
                              name="accountSettings.callWaiting"
                              formik={formik}
                              color="primary"
                              type="checkbox"
                            />
                          }
                          label="Enable Call Waiting"
                        />
                      </div>
                    </div>
                    {role !== Role.USER && (
                      <>
                        <div className="row borderBottom">
                          <div className="col-lg-8">
                            <h5 className="fieldTitle">Can Call Coach</h5>
                            <p className="fieldDescription">
                              Agent can monitor, whisper and join live calls.
                              Managers and admins have this ability regardless.
                            </p>
                          </div>
                          <div className="col-lg-4">
                            <FormControlLabel
                              control={
                                <FormikSwitch
                                  name="accountSettings.canCallCoach"
                                  formik={formik}
                                  color="primary"
                                  type="checkbox"
                                />
                              }
                              label="Can Call Coach"
                            />
                          </div>
                        </div>
                        <div className="row borderBottom">
                          <div className="col-lg-8">
                            <h5 className="fieldTitle">Can Be Call Coached</h5>
                            <p className="fieldDescription">
                              Agent's live calls can be monitored, whispered and
                              joined
                            </p>
                          </div>
                          <div className="col-lg-4">
                            <FormControlLabel
                              control={
                                <FormikSwitch
                                  name="accountSettings.canBeManaged"
                                  formik={formik}
                                  color="primary"
                                  type="checkbox"
                                />
                              }
                              label="Can Be Call Coached"
                            />
                          </div>
                        </div>
                      </>
                    )}
                    <div
                      className={`row ${
                        role !== Role.USER ? 'borderBottom' : ''
                      }`}
                    >
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">Expanded Notifications</h5>
                        <p className="fieldDescription">
                          Call notifications are expanded by default in
                          PowerCall
                        </p>
                      </div>
                      <div className="col-lg-4">
                        <FormControlLabel
                          control={
                            <FormikSwitch
                              name="accountSettings.defaultExtend"
                              formik={formik}
                              color="primary"
                              type="checkbox"
                            />
                          }
                          label="Expanded Notifications"
                        />
                      </div>
                    </div>

                    {role !== Role.USER && (
                      <div
                        className={`row ${
                          showPauseCallRecording && 'borderBottom'
                        }`}
                      >
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">DND Status</h5>
                          <p className="fieldDescription">
                            Turn agent's Do Not Disturb status ON or OFF
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormControlLabel
                            control={
                              <FormikSwitch
                                name="accountSettings.dnd"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                              />
                            }
                            label="DND Status"
                          />
                        </div>
                      </div>
                    )}
                    {showPauseCallRecording && role !== Role.USER && (
                      <div className="row">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">Allow Pause Recordings</h5>
                          <p className="fieldDescription">
                            User will be able to pause call recording in
                            Powercall
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormControlLabel
                            control={
                              <FormikSwitch
                                name="accountSettings.canPauseRecording"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                              />
                            }
                            label="Allow Pause Recording"
                          />
                        </div>
                      </div>
                    )}
                  </TabPanel>
                </WizardModal>
              </TabContext>
            </form>
            {!formik.isSubmitting && // submitting turns all fields to touched
              (formikTouched.current = formik.touched) && <></>}
            <DeleteAgentModal
              name={`${formik.values.accountSettings.fname} ${formik.values.accountSettings.lname}`}
              show={shows}
              handleClose={handleClose}
              userAtom={userAtomLoadable}
              enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
            />
            <ReplaceAgentModal
              handleClose={handleCloseR}
              show={showR}
              oldAgentName={`${myBusinessUsersCached.current?.fname} ${myBusinessUsersCached.current?.lname}`}
              newAgentName={`${formik.values.accountSettings.fname} ${formik.values.accountSettings.lname}`}
              handleSubmit={formik.handleSubmit}
              setFlagForReplace={setFlagForReplace}
            />
            <ConfirmAgentPowerlists
              title="Enable PowerDialer?"
              message={
                <>
                  <p>
                    Selecting a PowerDialer product will upgrade your billing
                    tier to Professional. Select Continue to proceed or Cancel
                    to go back.
                  </p>
                  <p>Do you want to continue?</p>
                </>
              }
              // handleClose={handleCloseDelete}
              handleClose={() => {
                // set powerdialer product back to none
                formik.setFieldValue('manage.powerlistProduct', 0);
                setShowUpgrade(false);
              }}
              handleSubmit={() => {
                // set billing tier to enterprise
                formik.setFieldValue('accountSettings.billingTier', 1);
                // close confirmation
                setShowUpgrade(false);
              }}
              show={showUpgrade}
            />
            <ConfirmAgentPowerlists
              title="Disable PowerDialer?"
              message={
                <>
                  <p>
                    Downgrading your billing tier will remove the PowerDialer
                    product. Select Continue to proceed or Cancel to go back.
                  </p>
                  <p>Do you want to continue?</p>
                </>
              }
              // handleClose={handleCloseDelete}
              handleClose={() => {
                // set billing tier back to enterprise
                formik.setFieldValue('accountSettings.billingTier', 1);
                setShowDowngrade(false);
              }}
              handleSubmit={() => {
                // set powerdialer product back to none
                formik.setFieldValue('manage.powerlistProduct', 0);
                // close confirmation
                setShowDowngrade(false);
              }}
              show={showDowngrade}
            />
            <ManageAgentProductsModal
              endpointEnum={endpointEnum}
              show={showManageAgentProducts}
              handleClose={() => {
                setShowManageAgentProducts(false);
                onHide();
              }}
              formikValues={formikValues}
            />
            <ProductChangesConfirmModal
              show={showProductChanges}
              handleClose={() => {
                setShowProductChanges(false);
                refetchActiveUserProducts();
              }}
              userProductChanges={userProductChanges}
              onHandleSubmit={(val) => {
                setProductChangesLoading(true);
                submitForm(val, formik.values);
                val === true && setShowManageAgentProducts(true);
              }}
              enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
              currentUser={myBusinessUsersCached?.current}
              productChangesLoading={productChangesLoading}
            />
          </>
        );
      }}
    </Formik>
  );
}
